import {siteConfig, copy} from '../site.js';
let stripeScript;
function loadStripe() {
    if (window.Stripe) return Promise.resolve();
    if (!stripeScript) stripeScript = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = 'https://js.stripe.com/v3/';
        const timer = setTimeout(() => script.onerror(), 20000);
        script.onload = () => { clearTimeout(timer); resolve(); };
        script.onerror = () => { clearTimeout(timer); script.remove(); stripeScript = null; reject(new Error('Payment options could not be loaded. Please reload to try again.')); };
        document.head.appendChild(script);
    });
    return stripeScript;
}
    const appearance = {
        theme: 'flat',
        variables: {
            colorPrimary: '#000000',
            colorBackground: '#fff',
            colorText: '#000000',
            colorTextSecondary: '#000000',
            borderRadius: '0px',
            buttonBorderRadius: '0px',
            buttonColorBackground: '#000000',
            buttonColorText: '#ffffff',
            fontFamily: '"Inter", sans-serif',
            iconColor: '#7f7f7f',
            accordionItemSpacing: '0',
            fontSizeXs: '14px'
        },
        rules: {
            '.AccordionItem': {
                border: '1px solid #bebebe',
                borderTopWidth: '0px',
                borderRadius: '0px',
                boxShadow: 'none',
                paddingLeft: '16px',
                paddingBottom: '26px',
                paddingTop: '26px',
                fontSize: '16px',
                fontWeight: '400',
                letterSpacing: '-.32px',
                color: '#000000',
            },
            '.AccordionItem--selected': {
                backgroundColor: '#f1f1f1',
                borderColor: '#bebebe',
                borderRadius: '0px',
                boxShadow: 'none'
            },
            '.AccordionItem:focus-visible': {
                boxShadow: 'none',
            },
            '.Input': {
                border: '1px solid #bebebe',
                borderRadius: '0px'
            },
            '.Input:focus': {
                borderColor: '#bebebe',
                boxShadow: 'none',
            },
            '.RadioIcon': {
                width: '20px'
            },
            '.RadioIconInner--checked': {
                r: '16px',
                fill: '#ffffff'
            },
            '.RadioIconOuter--checked': {
                strokeWidth: '50',
                stroke: '#000000'
            },
            '.RadioIconOuter': {
                strokeWidth: '4',
                stroke: '#bebebe'
            },
            '.Dropdown': {
                borderRadius: '0px',
                borderColor: '#bebebe'
            },
            '.Block': {
                borderRadius: '0px',
                borderColor: '#bebebe',
                boxShadow: 'none'
            },
            '.BlockAction': {
                borderRadius: '0px'
            },
            '.Link': {
                borderRadius: '0px'
            },
            '.PickerItem': {
                borderRadius: '0px',
                borderColor: '#bebebe',
                boxShadow: 'none'
            },
            '.PickerItem--selected': {
                borderRadius: '0px',
                borderColor: '#000000',
                backgroundColor: '#f1f1f1'
            },
            '.PickerAction': {
                borderRadius: '0px',
                borderColor: '#bebebe'
            },
            '.Action': {
                borderRadius: '0px'
            },
            '.Button': {
                borderRadius: '0px',
                backgroundColor: '#000000',
                color: '#ffffff'
            }
        },
    };

export class StripePayment {
    constructor(container) {
        this.container = container;
        this.secret = null;
        this.shell = container.closest('.checkout-payment-shell');
    }
    status(message = '', loading = false) {
        if (!this.shell) return;
        this.shell.setAttribute('aria-busy', String(loading));
        this.shell.classList.toggle('has-payment-message', !!message);
        this.shell.querySelector('.checkout-payment-status').hidden = !message;
        this.shell.querySelector('.checkout-payment-spinner').hidden = !loading;
        this.shell.querySelector('[data-payment-status-text]').textContent = copy(message);
    }
    stopLoading(message) {
        if (this.shell?.getAttribute('aria-busy') !== 'true') return;
        this.clear();
        this.status(message);
    }
    async update(markup) {
        try { await this.mount(markup); }
        catch (error) {
            if (!this.secret) { this.clear(); this.status(error.message); }
            throw error;
        }
    }
    async mount(markup) {
        const document = new DOMParser().parseFromString(markup, 'text/html');
        const node = document.querySelector('[data-stripe-config]');
        if (!node) throw new Error(copy('Payment options are currently unavailable. Please try again.'));
        this.config = JSON.parse(node.textContent);
        const returnUrl = new URL(this.config.returnUrl, window.location.origin);
        returnUrl.searchParams.delete('step');
        this.config.returnUrl = returnUrl.href;
        if (this.secret === this.config.secret) {
            const result = await this.elements.fetchUpdates();
            if (result.error) throw new Error(result.error.message);
            return;
        }
        this.clear();
        this.status('Loading payment methods…', true);
        await loadStripe();
        this.stripe = window.Stripe(this.config.key);
        this.elements = this.stripe.elements({clientSecret: this.config.secret, appearance, locale:siteConfig().language || 'en',
            fonts:[{family:'Inter', src:'url(https://novastars.nl/static/default/fonts/Inter_24pt-Regular.ttf)', weight:'400'}]});
        const element = this.elements.create('payment', {
            layout: {type:'accordion', defaultCollapsed:false, radios:'always', spacedAccordionItems:true, visibleAccordionItemsCount:3},
            paymentMethodOrder:['ideal','card','klarna'],
            fields:{billingDetails:'never'},
            defaultValues:{billingDetails:this.config.billing}
        });
        this.element = element;
        element.on('change', event => this.container.classList.toggle('is-link', event.value?.type === 'link'));
        await new Promise((resolve,reject) => {
            const timer = setTimeout(() => reject(new Error('Payment options are taking too long to load. Please try again.')), 20000);
            element.on('ready', () => {clearTimeout(timer);resolve();});
            element.on('loaderror', () => {clearTimeout(timer);reject(new Error('Payment options could not be loaded. Please try again.'));});
            element.mount(this.container);
        });
        this.secret = this.config.secret;
        this.status();
    }
    clear() {
        if (this.element) this.element.destroy();
        this.element = null; this.secret = null;
        this.container.classList.remove('is-link');
        this.container.replaceChildren();
        this.status();
    }
    async confirm() {
        if (!this.secret) throw new Error(copy('Payment options are not ready yet.'));
        const result = await this.stripe.confirmPayment({elements:this.elements, confirmParams:{
            return_url:this.config.returnUrl, payment_method_data:{billing_details:this.config.billing}
        }});
        if (result.error) throw new Error(result.error.message || 'Your payment could not be completed. Please try again.');
    }
}
